/**
 * Scheduler utility that uses the Web Scheduler API when available,
 * with a fallback to setTimeout to maintain compatibility.
 * TODO: Move this somewhere else
 */

type SchedulerCallback = () => void;

interface SchedulerOptions {
  priority?: "user-blocking" | "user-visible" | "background";
}

// Type definitions for the Scheduler API
interface SchedulerPostTaskOptions {
  priority?: "user-blocking" | "user-visible" | "background";
  signal?: AbortSignal;
}

interface Scheduler {
  postTask: (callback: () => void, options?: SchedulerPostTaskOptions) => Promise<void>;
  yield: () => Promise<void>;
}

declare global {
  interface Window {
    scheduler: Scheduler;
  }
}

/**
 * Check if the Scheduler API is available
 */
function isSchedulerAvailable(): boolean {
  return (
    typeof window !== "undefined" &&
    "scheduler" in window &&
    Boolean(window.scheduler) &&
    typeof window.scheduler === "object" &&
    window.scheduler !== null &&
    "postTask" in window.scheduler
  );
}

/**
 * Schedule a task using the Scheduler API or setTimeout fallback
 */
export function scheduleTask(
  callback: SchedulerCallback,
  options: SchedulerOptions = {}
): AbortController {
  const controller = new AbortController();

  if (isSchedulerAvailable()) {
    try {
      window.scheduler
        .postTask(callback, {
          priority: options.priority || "user-visible",
          signal: controller.signal,
        })
        .catch((error: Error) => {
          // Task was aborted or failed
          if (error.name !== "AbortError") {
            console.warn("Scheduler task failed:", error);
          }
        });
    } catch (error) {
      // Fallback to setTimeout if scheduler fails
      console.warn("Scheduler API failed, falling back to setTimeout:", error);
      const timeoutId = setTimeout(() => {
        if (!controller.signal.aborted) {
          callback();
        }
      }, 0);

      controller.signal.addEventListener("abort", () => {
        clearTimeout(timeoutId);
      });
    }
  } else {
    // Fallback to setTimeout
    const timeoutId = setTimeout(() => {
      if (!controller.signal.aborted) {
        callback();
      }
    }, 0);

    controller.signal.addEventListener("abort", () => {
      clearTimeout(timeoutId);
    });
  }

  return controller;
}

export function scheduleYield() {
  if (window.scheduler?.yield) {
    return window.scheduler.yield();
  }
  return new Promise<void>((resolve) => {
    queueMicrotask(() => {
      resolve();
    });
  });
}

type YieldLoopFn = (() => Promise<void> | undefined) & { reset: VoidFunction };

export function scheduleYieldLoop(ms = 45) {
  let prev = performance.now();
  const fn = () => {
    if (performance.now() - prev > ms) {
      return scheduleYield().then(() => {
        prev = performance.now();
      });
    }
    return undefined;
  };
  Object.assign(fn, {
    reset: () => {
      prev = performance.now();
    },
  });
  // oxlint-disable-next-line endla/no-double-type-assertion -- Existing code
  return fn as unknown as YieldLoopFn;
}
